import { apiClient } from "./auth";
import type {
  ExpirePatientPayload,
  ExpirePatientResponse,
} from "../Interfaces/PatientInterface";

export interface Patient {
  id: string;
  tenantId: string;
  uhid: string | null;
  /** HIS numeric registration / patient id (used as patientId when booking) */
  registrationId?: number | null;
  primaryPhone: string | null;
  /** CRM-only alternate calling number (CAR-2844). Not used for phone matching/dedup. */
  displayNumber?: string | null;
  secondaryPhone: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  email: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  age: number | null;
  city: string | null;
  state: string | null;
  addressLine1: string | null;
  pincode: string | null;
  preferredLanguage: string | null;
  whatsappNumber?: string | null;
  primaryFacilityId: string | null;
  leadSource: string | null;
  utmSource: string | null;
  utmCampaign: string | null;
  payerType?: string | null;
  /** True when patient phone failed validation at ingest (CAR-1237). */
  phoneInvalid?: boolean | null;
  isVip: boolean | null;
  doNotCall: boolean | null;
  doNotMessage: boolean | null;
  whatsappOptOut: boolean | null;
  isDeleted: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * CRM-only patient fields for PATCH /consumer/patients/:id (CAR-2844).
 * Does not touch registered primaryPhone or push to MHEA/HIS.
 */
export interface PatchPatientCrmPayload {
  displayNumber?: string | null;
  whatsappNumber?: string | null;
  email?: string | null;
  primaryFacilityId?: string | null;
}

export interface PatchPatientResponse {
  success: boolean;
  data: Patient;
}

/**
 * PATCH /consumer/patients/:id — persist CRM-only patient fields.
 * Registered phone cannot be changed via this API.
 */
export const patchPatientApi = async (
  patientId: string,
  payload: PatchPatientCrmPayload
): Promise<PatchPatientResponse> => {
  const response = await apiClient.patch<
    PatchPatientCrmPayload,
    PatchPatientResponse
  >(`/consumer/patients/${encodeURIComponent(patientId)}`, payload);
  return response.data;
};

export interface PatientResponse {
  success: boolean;
  data: Patient;
}

export const getPatientByIdApi = async (
  id: string
): Promise<PatientResponse> => {
  const response = await apiClient.get<PatientResponse>(
    `/consumer/patients/${id}`
  );
  return response.data;
};

export interface CreatePatientPayload {
  /** UHID from external-appointment/patients/register response (when calling after register) */
  UHID?: string;
  Patient_First_Name: string;
  Middle_Name: string;
  last_name: string;
  Contact_no: string;
  WhatsApp_number?: string;
  /** Omitted on Add New Contact when the agent leaves Gender blank (CAR-2816). */
  gender?: string;
  Email_id: string;
  DOB?: string;
  primaryFacilityId?: string;
  /** Lead/contact metadata (optional; backend may ignore unknown fields) */
  utmSource?: string;
  leadSource?: string;
  sourceChannel?: string;
  /** Legacy campaign field; keep so older HIS/contact upserts still accept it. */
  utmCampaign?: string;
  preferredUnit?: string;
  payerType?: string;
  opportunityType?: string;
  /** Omitted on Add New Contact when every address field is blank (CAR-2816). */
  address?: {
    Address: string;
    pincode: string;
    City: string;
    State: string;
    Country?: string;
  };
}

/** Response from POST /consumer/upsert - backend may return contactId (new contact) or patientId (patient upsert) */
export interface ConsumerUpsertResponse {
  success: boolean;
  data: {
    kind?: string;
    patientId?: string;
    contactId?: string;
    identifierId?: string;
    linkedContactId?: string;
    created?: boolean;
  };
}

export const createPatientApi = async (
  payload: CreatePatientPayload
): Promise<ConsumerUpsertResponse> => {
  const response = await apiClient.post<
    CreatePatientPayload,
    ConsumerUpsertResponse
  >("/consumer/upsert", payload);
  return response.data;
};

/**
 * Mark a patient as expired.
 * POST /consumer/patients/{patientId}/expire
 */
export const expirePatientApi = async (
  patientId: string
): Promise<ExpirePatientResponse> => {
  const payload: ExpirePatientPayload = {};
  const response = await apiClient.post<
    ExpirePatientPayload,
    ExpirePatientResponse
  >(`/consumer/patients/${encodeURIComponent(patientId)}/expire`, payload);
  return response.data;
};

export type PatientHisSearchSource =
  "local" | "his" | "not_found" | "his_disabled";

export interface PatientHisSearchRecord {
  id: string;
  tenantId: string;
  uhid: string | null;
  primaryPhone: string;
  fullName: string | null;
  firstName: string | null;
  lastName: string | null;
  age: number | null;
  gender: "male" | "female" | "other" | null;
  dateOfBirth: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  primaryFacilityId: string | null;
  isVip: boolean;
  doNotCall: boolean;
  doNotMessage: boolean;
  createdAt: string;
  updatedAt: string;
}

/** HIS preview candidate (not yet imported into CarePro) */
export interface PatientHisPreviewRecord {
  registrationNo: string;
  fullName: string;
  age: number | null;
  gender: "male" | "female" | null;
  /** The searched phone — what the frontend passes back on import ("" for a UHID search). */
  phone: string;
  /** Mobile on the HIS record. Shown in the preview. For UHID imports the backend stores this as primaryPhone when phone is "" (CAR-2947). Omitted when HIS sends none. */
  hisMobile?: string;
}

export interface PatientHisSearchResponse {
  success: boolean;
  data: {
    source: PatientHisSearchSource;
    patients: PatientHisSearchRecord[];
    importedFromHis: number;
    count: number;
    hisPreview?: PatientHisPreviewRecord[];
  };
}

export interface PatientHisSearchOptions {
  /** When true, returns preview candidates without auto-import (Universal Search) */
  preview?: boolean;
}

export async function searchPatientsWithHisApi(
  phone: string,
  options?: PatientHisSearchOptions
): Promise<PatientHisSearchResponse> {
  const params: Record<string, string | boolean> = { phone };
  if (options?.preview) {
    params.preview = true;
  }
  const response = await apiClient.get<PatientHisSearchResponse>(
    "/consumer/patients/search-with-his",
    { params }
  );
  return response.data;
}

export interface FetchPatientFromHisByUhidResponse {
  success: boolean;
  data: {
    source?: PatientHisSearchSource;
    patient: Patient | null;
    importedFromHis?: boolean;
    /** Present only for preview calls that matched in HIS (CAR-2947) */
    hisPreview?: PatientHisPreviewRecord[];
  };
}

/**
 * Look up / import a HIS patient by registration number (UHID).
 * GET /consumer/patients/search-with-his-by-uhid?registrationNo=
 * With `preview`, an HIS match is returned in `hisPreview` without importing (CAR-2947).
 */
export async function fetchPatientFromHisByUhidApi(
  registrationNo: string,
  phone?: string,
  options?: PatientHisSearchOptions
): Promise<FetchPatientFromHisByUhidResponse> {
  const params: Record<string, string | boolean> = { registrationNo };
  if (phone?.trim()) {
    params.phone = phone.trim();
  }
  if (options?.preview) {
    params.preview = true;
  }
  const response = await apiClient.get<FetchPatientFromHisByUhidResponse>(
    "/consumer/patients/search-with-his-by-uhid",
    { params }
  );
  return response.data;
}
